import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useUser, useClerk } from '@clerk/clerk-react';
import { UserCircle2, Bell, Trash2, ChevronRight, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import API_BASE_URL from '../../config/api';

const TOGGLE_META = [
  {
    key: 'dd_reminders',
    title: 'Due Diligence Reminders',
    desc: 'Get notified 3 days and 1 day before due diligence deadlines',
  },
  {
    key: 'closing_reminders',
    title: 'Closing Reminders',
    desc: 'Get notified 5 days and 2 days before closing dates',
  },
  {
    key: 'ai_insights',
    title: 'AI Coach Insights',
    desc: 'Daily AI-powered business insights and priorities',
  },
];

const MobileAccountSection = () => {
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const { signOut, openUserProfile } = useClerk();

  const [prefs, setPrefs] = useState({
    dd_reminders: true,
    closing_reminders: true,
    ai_insights: true,
  });
  const [savingKey, setSavingKey] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Load notification preferences on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/user/notification-preferences`, {
          withCredentials: true,
        });
        if (!cancelled && data?.notifications) {
          setPrefs({
            dd_reminders: !!data.notifications.dd_reminders,
            closing_reminders: !!data.notifications.closing_reminders,
            ai_insights: !!data.notifications.ai_insights,
          });
        }
      } catch (e) {
        console.warn('Failed to load notification preferences:', e?.response?.status, e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (key, next) => {
    const previous = prefs[key];
    setPrefs((p) => ({ ...p, [key]: next }));
    setSavingKey(key);
    try {
      await axios.put(
        `${API_BASE_URL}/api/user/notification-preferences`,
        { ...prefs, [key]: next },
        { withCredentials: true },
      );
    } catch (e) {
      // Roll back on failure
      setPrefs((p) => ({ ...p, [key]: previous }));
      toast.error('Could not save preference. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleManageAccount = () => {
    // Clerk's hosted profile modal — user is already authenticated, no re-login required
    if (typeof openUserProfile === 'function') {
      openUserProfile();
    } else {
      toast.error('Account management is not available right now.');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/account/delete`, { withCredentials: true });

      // Clear local state and sign out of Clerk
      try { localStorage.clear(); } catch (_) { /* ignore */ }
      try { sessionStorage.clear(); } catch (_) { /* ignore */ }
      try { await signOut(); } catch (_) { /* ignore — user record is already gone */ }

      toast.success('Your account has been deleted.');
      navigate('/account-deleted', { replace: true });
    } catch (e) {
      console.error('Account deletion failed:', e);
      toast.error(
        'Something went wrong. Please try again or contact support at byron@ineednumbers.com',
      );
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-0" data-testid="mobile-account-section">
      {/* C. Manage Account */}
      <button
        type="button"
        onClick={handleManageAccount}
        className="w-full flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left active:bg-gray-50"
        data-testid="manage-account-btn"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <UserCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">Manage Account</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Update your email, password, or sign-in methods
            </div>
            {clerkUser?.primaryEmailAddress?.emailAddress && (
              <div className="text-[11px] text-gray-400 mt-1">
                {clerkUser.primaryEmailAddress.emailAddress}
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </button>

      {/* D. Push Notifications — iOS-style grouped list */}
      <div className="mt-6">
        {/* Section header — small gray uppercase label, outside the card, with bell icon */}
        <div className="px-1 mb-2 flex items-center gap-1.5">
          <Bell className="w-3.5 h-3.5 text-gray-500" />
          <h3 className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
            Push Notifications
          </h3>
        </div>

        {/* Each toggle row lives inside the same rounded card, separated by thin dividers */}
        <div className="rounded-xl bg-white border border-gray-200 overflow-hidden">
          {TOGGLE_META.map(({ key, title, desc }, idx) => (
            <div
              key={key}
              className={`flex items-center gap-4 px-4 py-4 ${
                idx > 0 ? 'border-t border-gray-100' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-medium text-gray-900 leading-tight">
                  {title}
                </div>
                <div className="text-[13px] text-gray-500 mt-1 leading-snug">
                  {desc}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {savingKey === key && (
                  <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                )}
                <Switch
                  checked={prefs[key]}
                  onCheckedChange={(checked) => handleToggle(key, checked)}
                  data-testid={`notif-toggle-${key}`}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* E. Delete Account — visually separated with 32px gap + divider */}
      <div className="mt-8 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="text-red-600 font-semibold text-sm py-2"
          data-testid="delete-account-btn"
        >
          Delete Account
        </button>
        <div className="text-xs text-gray-500 mt-1">
          Permanently delete your account and all data
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={(o) => !deleting && setConfirmOpen(o)}>
        <AlertDialogContent data-testid="delete-account-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Your Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your account and all your data, including your deals,
              financial records, and settings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-confirm-btn"
            >
              {deleting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting your account…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Delete Account
                </span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Loading overlay during deletion (covers the whole settings page) */}
      <Dialog open={deleting}>
        <DialogContent className="sm:max-w-sm" data-testid="deleting-overlay">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-red-600" />
              Deleting your account…
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            We're removing your data from our servers. This will only take a few seconds.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileAccountSection;
